import { formatStudioDay } from '@/utils/studioDay';
import { depositAppointmentIso, isInsideCancelWindow } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { hasMoneyIn } from '@/domain/depositFlow';
import type { Deposit, PaybyExtensionRequest } from '@/domain/types';

// ---------------------------------------------------------------------------
// Pay-by extension on an UNPAID deposit link (W5 i5, board 09 — DECIDED 24 Sep).
// A client asks for more days to pay, keeping the same appointment:
//   - appointment MORE than 7 days out -> the link's follow-up owner extends it
//     directly (no approval);
//   - INSIDE 7 days -> a PaybyExtensionRequest Ops approves or declines (mirrors
//     inside-7-day top-up extensions).
// Approve: deposit.expiresAt moves — that IS the held booking's hold (the
// reconciler releases the slot off expiresAt) — and the reminder re-points to
// the new date (an attached top-up instalment's due date + the deposit's
// reminder stamp), board 09 "Extending re-points the reminder". Decline: the
// link keeps its original date and lapses as normal (-> its follow-up owner).
// The 7-day line is the shared cancel split's (whole studio calendar days).
// Mutations follow the *Flow convention; records persist through the bridge.
// ---------------------------------------------------------------------------

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

export interface RequestPaybyExtensionInput {
  depositId: string;
  newPayByIso: string;
  /** The person asking (the link's follow-up owner). */
  by: string;
  note?: string;
  at?: string;
}

export type RequestPaybyExtensionResult =
  | { ok: true; mode: 'extended'; depositId: string; previousPayByIso: string; newPayByIso: string }
  | { ok: true; mode: 'requested'; requestId: string }
  | {
      ok: false;
      error: 'no-deposit' | 'not-unpaid' | 'part-paid' | 'not-later' | 'after-appointment' | 'already-requested';
    };

export interface ResolvePaybyExtensionInput {
  requestId: string;
  /** Ops. */
  by: string;
  note?: string;
  at?: string;
  /**
   * The live (ops-view) copies of the request and its link. The Ops desk reads
   * the server view, which can be fresher than this tab's hydrated store (an ask
   * raised in another tab); when passed they are the truth the decision acts on.
   */
  request?: PaybyExtensionRequest;
  deposit?: Deposit;
}

export type ResolvePaybyExtensionResult =
  | { ok: true; requestId: string }
  | { ok: false; error: 'no-request' | 'not-pending' | 'link-closed' };

/**
 * The appointment the link is for (@atink/domain depositAppointmentIso): its held
 * booking, else the booking it is linked to (a bookingId-only link, e.g.
 * dep-seed-partial), else the pinned slot. Undefined = fail closed (Ops decides).
 */
export function paybyAppointmentIso(d: Deposit): string | undefined {
  return depositAppointmentIso(d, useBrainStore.getState().bookings);
}

/** Deterministic request id for one ask on one link. */
export function paybyRequestId(depositId: string, atIso: string): string {
  return `payby-${depositId}-${Date.parse(atIso)}`;
}

/**
 * Move an unpaid link's pay-by out and re-point its reminder: deposit.expiresAt
 * (the hold) -> the new date, the reminder stamp cleared, and an attached active
 * top-up plan's current unsettled instalment re-armed on the new date.
 */
function extendPayBy(d: Deposit, newPayByIso: string): void {
  const brain = useBrainStore.getState();
  brain.upsertDeposit({ ...d, expiresAt: newPayByIso, lastTopupReminderAt: undefined });
  const plan = Object.values(brain.topupPlans).find((p) => p.depositId === d.id && p.status === 'active');
  if (!plan) return;
  const idx = plan.instalments.findIndex((i) => i.status !== 'paid' && i.status !== 'cancelled');
  if (idx === -1) return;
  const current = plan.instalments[idx]!;
  if (Date.parse(current.dueIso) >= Date.parse(newPayByIso)) return; // already later — leave it
  brain.upsertTopupPlan({
    ...plan,
    instalments: plan.instalments.map((inst, i) =>
      i === idx ? { ...inst, dueIso: newPayByIso, status: 'scheduled' as const, remindedAt: undefined } : inst,
    ),
  });
}

function logExtended(d: Deposit, newPayByIso: string, by: string, at: string, how: string): void {
  useBrainStore.getState().appendActivity({
    id: `act-payby-${d.id}-${Date.parse(at)}`,
    type: 'deposit-link-sent',
    customerId: d.customerId,
    studioId: d.studioId,
    ...(d.artistId ? { artistId: d.artistId } : {}),
    body: `Pay-by extended to ${formatStudioDay(newPayByIso, 'EEE d MMM')} (${how}) by ${by} - same appointment, reminder re-pointed.`,
    timestamp: at,
  });
}

/**
 * A client asks for more days to pay an unpaid link. Outside 7 days it is
 * extended at once; inside 7 days an Ops request is raised and the link keeps
 * its date until Ops decides.
 */
export function requestPaybyExtension(input: RequestPaybyExtensionInput): RequestPaybyExtensionResult {
  const brain = useBrainStore.getState();
  const d = brain.deposits[input.depositId];
  if (!d) return { ok: false, error: 'no-deposit' };
  if (d.status !== 'pending') return { ok: false, error: 'not-unpaid' };
  // Q5 (DECIDED 24 Sep): money already in = a part-paid top-up, which moves on the
  // top-up extension (Agent outside 7 days, Ops inside), never on pay-by.
  if (hasMoneyIn(d, brain.topupPlans)) return { ok: false, error: 'part-paid' };
  if (Date.parse(input.newPayByIso) <= Date.parse(d.expiresAt)) return { ok: false, error: 'not-later' };
  const appt = paybyAppointmentIso(d);
  // Same appointment, more days to pay: the pay-by can never reach the appointment.
  if (appt && Date.parse(input.newPayByIso) >= Date.parse(appt)) return { ok: false, error: 'after-appointment' };
  if (Object.values(brain.paybyExtensionRequests).some((r) => r.depositId === d.id && r.status === 'pending')) {
    return { ok: false, error: 'already-requested' };
  }
  const at = input.at ?? nowIso();

  // Only a KNOWN appointment more than 7 days out extends directly; an unresolved
  // one fails closed to an Ops request (tv-2na.12.13).
  if (appt && !isInsideCancelWindow(appt, at)) {
    extendPayBy(d, input.newPayByIso);
    logExtended(d, input.newPayByIso, input.by, at, 'more than 7 days out');
    eventBus.publish('ops:pay-by-extended', {
      depositId: d.id,
      customerId: d.customerId,
      previousDueIso: d.expiresAt,
      newDueIso: input.newPayByIso,
      at,
    });
    return { ok: true, mode: 'extended', depositId: d.id, previousPayByIso: d.expiresAt, newPayByIso: input.newPayByIso };
  }

  const request: PaybyExtensionRequest = {
    id: paybyRequestId(d.id, at),
    depositId: d.id,
    customerId: d.customerId,
    studioId: d.studioId,
    ...((d.heldBookingId ?? d.bookingId) ? { bookingId: d.heldBookingId ?? d.bookingId } : {}),
    currentPayByIso: d.expiresAt,
    requestedPayByIso: input.newPayByIso,
    requestedBy: input.by,
    ...(input.note?.trim() ? { note: input.note.trim() } : {}),
    status: 'pending',
    createdAt: at,
  };
  brain.upsertPaybyExtensionRequest(request);
  return { ok: true, mode: 'requested', requestId: request.id };
}

function pendingRequest(input: ResolvePaybyExtensionInput): PaybyExtensionRequest | ResolvePaybyExtensionResult {
  const req = input.request ?? useBrainStore.getState().paybyExtensionRequests[input.requestId];
  if (!req) return { ok: false, error: 'no-request' };
  if (req.status !== 'pending') return { ok: false, error: 'not-pending' };
  return req;
}

/** Ops approves an inside-7-day ask: the pay-by (and the hold) move, the reminder re-points. */
export function approvePaybyExtension(input: ResolvePaybyExtensionInput): ResolvePaybyExtensionResult {
  const req = pendingRequest(input);
  if ('ok' in req) return req;
  const brain = useBrainStore.getState();
  const d = input.deposit ?? brain.deposits[req.depositId];
  if (!d || d.status !== 'pending') return { ok: false, error: 'link-closed' };
  const at = input.at ?? nowIso();
  extendPayBy(d, req.requestedPayByIso);
  brain.upsertPaybyExtensionRequest({
    ...req,
    status: 'approved',
    resolvedBy: input.by,
    resolvedAt: at,
    ...(input.note?.trim() ? { resolutionNote: input.note.trim() } : {}),
  });
  logExtended(d, req.requestedPayByIso, input.by, at, 'approved by Ops, inside 7 days');
  eventBus.publish('ops:pay-by-extended', {
    depositId: d.id,
    customerId: d.customerId,
    previousDueIso: d.expiresAt,
    newDueIso: req.requestedPayByIso,
    at,
  });
  return { ok: true, requestId: req.id };
}

/** Ops declines: the link keeps its original pay-by and lapses as normal. */
export function declinePaybyExtension(input: ResolvePaybyExtensionInput): ResolvePaybyExtensionResult {
  const req = pendingRequest(input);
  if ('ok' in req) return req;
  const at = input.at ?? nowIso();
  useBrainStore.getState().upsertPaybyExtensionRequest({
    ...req,
    status: 'declined',
    resolvedBy: input.by,
    resolvedAt: at,
    ...(input.note?.trim() ? { resolutionNote: input.note.trim() } : {}),
  });
  return { ok: true, requestId: req.id };
}
